/*
 * Copyright (c) 2022. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */
import { FormControlLabel, Switch, type SwitchProps, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useCallback, useMemo } from 'react';
import type { PAFormikListType, PAFormikType } from './types';

// biome-ignore lint/style/useComponentExportOnlyModules: this will be fixed after switch away from PAFormik
export const IOSSwitch = styled((props: SwitchProps) => (
  <Switch focusVisibleClassName=".Mui-focusVisible" disableRipple {...props} />
))(({ theme }) => ({
  width: 42,
  height: 20,
  maxHeight: 20,
  padding: 0,
  marginRight: 10,
  '& .MuiSwitch-switchBase': {
    padding: 0,
    margin: 2,
    transitionDuration: '200ms',
    transitionTimingFunction: 'cubic-bezier(0.4, 0, 0.2, 1)',
    '&.Mui-checked': {
      transform: 'translateX(22px)',
      color: '#fff',
      '& + .MuiSwitch-track': {
        backgroundColor: theme.palette.mode === 'dark' ? '#2ECA45' : '#65C466',
        opacity: 1,
        border: 0,
      },
      '&.Mui-disabled + .MuiSwitch-track': {
        opacity: 0.4,
      },
    },
    '&.Mui-focusVisible .MuiSwitch-thumb': {
      color: '#33cf4d',
      border: '6px solid #fff',
    },
    '&.Mui-disabled .MuiSwitch-thumb': {
      color: theme.palette.mode === 'light' ? theme.palette.grey[100] : theme.palette.grey[600],
    },
    '&.Mui-disabled + .MuiSwitch-track': {
      opacity: theme.palette.mode === 'light' ? 0.5 : 0.2,
    },
  },
  '& .MuiSwitch-thumb': {
    boxSizing: 'border-box',
    width: 16,
    height: 16,
  },
  '& .MuiSwitch-track': {
    borderRadius: 10,
    backgroundColor: theme.palette.mode === 'light' ? '#E9E9EA' : '#39393D',
    opacity: 1,
    transition: theme.transitions.create(['background-color'], {
      duration: 200,
      easing: 'cubic-bezier(0.4, 0, 0.2, 1)',
    }),
  },
}));

interface Props<T> {
  formik: PAFormikType<T> | PAFormikListType<T>;
  name: keyof T & string;
  label?: string;
  fontSize?: number;
  onChange?: (value: boolean) => void;
  disabled?: boolean;
}

export function PAFormikSwitch<T>({
  name,
  formik,
  label,
  fontSize,
  onChange,
  disabled,
}: Readonly<Props<T>>) {
  const index = 'index' in formik ? formik.index : undefined;
  const isChecked =
    (('index' in formik ? formik.values[formik.index][name] : formik.values[name]) as unknown as
      | boolean
      | undefined) ?? false;
  const { setFieldValue } = formik;

  const handleChange = useCallback(() => {
    const newValue = !isChecked as T[keyof T];
    setFieldValue(name, newValue).then(() => onChange?.(newValue as boolean));
  }, [isChecked, name, setFieldValue, onChange]);

  return useMemo(
    () => (
      <>
        {label ? (
          <FormControlLabel
            control={
              <IOSSwitch
                checked={isChecked}
                onChange={handleChange}
                data-testid={index !== undefined ? `${name}-${index}` : name}
              />
            }
            label={
              <Typography
                sx={{ color: isChecked ? undefined : 'text.secondary', fontSize: fontSize }}
              >
                {label}
              </Typography>
            }
            disabled={disabled}
            sx={{ ml: 0 }}
          />
        ) : (
          <IOSSwitch
            checked={isChecked}
            onChange={handleChange}
            data-testid={index !== undefined ? `${name}-${index}` : name}
          />
        )}
      </>
    ),
    [isChecked, handleChange, index, name, fontSize, label, disabled],
  );
}
